(() => {
  const SELECTOR = 'variant-selects.emarville-variant-picker';
  const READY = 'data-emarville-dependent-ready';

  class EmarvilleDependentVariants {
    constructor(root) {
      this.root = root;
      this.variants = this.readVariants();
      this.groups = Array.from(root.querySelectorAll('.emarville-variant-option'))
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition));
      this.resolving = false;

      if (!this.variants.length || !this.groups.length) return;

      root.setAttribute(READY, 'true');

      // Capture phase lets us resolve downstream options before Dawn sees
      // the same native change event. We never cancel or replace the event.
      root.addEventListener('change', this.onChange, true);

      const changed = this.resolveFrom(0);
      this.updateLabels();

      if (changed) {
        queueMicrotask(() => changed.dispatchEvent(new Event('change', { bubbles: true })));
      }
    }

    readVariants() {
      try {
        const node = this.root.querySelector('[data-emarville-variants]');
        const data = JSON.parse(node?.textContent || '[]');
        return Array.isArray(data) ? data : [];
      } catch {
        return [];
      }
    }

    onChange = (event) => {
      if (this.resolving) return;

      const control = event.target.closest('input[type="radio"], select');
      if (!control || !this.root.contains(control)) return;

      const group = control.closest('.emarville-variant-option');
      if (!group) return;

      this.resolving = true;
      try {
        const changedIndex = Number(group.dataset.optionPosition || 1) - 1;
        this.resolveFrom(changedIndex + 1);
        this.updateLabels();
      } finally {
        this.resolving = false;
      }
    };

    resolveFrom(startIndex) {
      let changed = null;

      for (let index = startIndex; index < this.groups.length; index += 1) {
        const group = this.groups[index];
        const prior = this.getSelectedValues().slice(0, index);
        const controls = this.getControls(group);

        let firstAvailable = null;
        let firstExisting = null;

        for (const control of controls) {
          const matches = this.variants.filter((variant) => {
            const opts = Array.isArray(variant.options)
              ? variant.options
              : [variant.option1, variant.option2, variant.option3].filter((v) => v != null);

            return prior.every((value, i) => opts[i] === value) && opts[index] === control.value;
          });

          const exists = matches.length > 0;
          const available = matches.some((variant) => variant.available);

          this.setState(control, exists, available);

          if (exists && !firstExisting) firstExisting = control;
          if (available && !firstAvailable) firstAvailable = control;
        }

        const current = this.getSelectedControl(group);
        const valid =
          current &&
          current.dataset.emarvilleExists === 'true' &&
          current.dataset.emarvilleAvailable === 'true';

        if (!valid) {
          const replacement = firstAvailable || firstExisting;

          if (replacement && replacement !== current) {
            this.select(replacement);
            changed = replacement.tagName === 'OPTION' ? replacement.parentElement : replacement;
          }
        }
      }

      return changed;
    }

    getControls(group) {
      const select = group.querySelector('select');
      return select
        ? Array.from(select.options)
        : Array.from(group.querySelectorAll('input[type="radio"]'));
    }

    getSelectedValues() {
      return this.groups.map((group) => {
        const select = group.querySelector('select');
        return select
          ? select.value
          : (group.querySelector('input[type="radio"]:checked')?.value || '');
      });
    }

    getSelectedControl(group) {
      const select = group.querySelector('select');
      return select
        ? (select.selectedOptions[0] || null)
        : group.querySelector('input[type="radio"]:checked');
    }

    select(control) {
      if (control.tagName === 'OPTION') {
        control.parentElement.value = control.value;
      } else {
        control.checked = true;
      }
    }

    setState(control, exists, available) {
      const disabled = !available;

      control.disabled = disabled;
      control.classList.toggle('disabled', disabled);
      control.classList.toggle('visually-disabled', disabled);
      control.dataset.emarvilleExists = String(exists);
      control.dataset.emarvilleAvailable = String(available);

      if (control.tagName === 'OPTION') {
        control.hidden = false;
        return;
      }

      const label = this.root.querySelector(`label[for="${CSS.escape(control.id)}"]`);

      control.hidden = false;
      label?.classList.remove('emarville-option-hidden');
      label?.classList.toggle('emarville-option-unavailable', disabled);
      label?.classList.toggle('emarville-option-nonexistent', !exists);
      label?.setAttribute('aria-disabled', String(disabled));
    }

    updateLabels() {
      for (const group of this.groups) {
        const value =
          group.querySelector('select')?.value ||
          group.querySelector('input[type="radio"]:checked')?.value ||
          '';

        const label = group.querySelector('[data-selected-value]');
        if (label) label.textContent = value;
      }
    }
  }


  const initVisualSelectors = (scope = document) => {
    scope.querySelectorAll?.('[data-emarville-variant-open]').forEach((button) => {
      if (button.dataset.emarvilleBound === 'true') return;
      button.dataset.emarvilleBound = 'true';

      button.addEventListener('click', () => {
        const dialog = document.getElementById(button.dataset.emarvilleVariantOpen);
        if (!dialog) return;

        if (typeof dialog.showModal === 'function') {
          dialog.showModal();
          dialog.scrollTop = 0;
          dialog.querySelector('.emarville-variant-modal__body')?.scrollTo({ top: 0, behavior: 'instant' });
          document.documentElement.classList.add('emarville-variant-modal-open');

          requestAnimationFrame(() => {
            dialog.classList.add('is-open');
          });
        }
      });
    });

    scope.querySelectorAll?.('[data-emarville-variant-modal]').forEach((dialog) => {
      if (dialog.dataset.emarvilleBound === 'true') return;
      dialog.dataset.emarvilleBound = 'true';

      const close = () => {
        dialog.classList.remove('is-open');

        if (dialog.open) {
          setTimeout(() => {
            if (dialog.open) dialog.close();
          }, 120);
        }

        document.documentElement.classList.remove('emarville-variant-modal-open');
      };

      dialog.querySelector('[data-emarville-variant-close]')?.addEventListener('click', close);

      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) close();
      });

      dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('emarville-variant-modal-open');
      });

      dialog.addEventListener('change', (event) => {
        if (event.target.matches('input[type="radio"]')) {
          requestAnimationFrame(close);
        }
      });

      const search = dialog.querySelector('[data-emarville-variant-search]');
      if (search) {
        search.addEventListener('input', () => {
          const query = search.value.trim().toLowerCase();

          dialog.querySelectorAll('[data-emarville-variant-card]').forEach((card) => {
            const input = card.previousElementSibling;
            const match = !query || card.dataset.value.includes(query);

            card.classList.toggle('emarville-variant-search-hidden', !match);
            if (input?.matches('input[type="radio"]')) {
              input.classList.toggle('emarville-variant-search-hidden', !match);
            }
          });
        });
      }
    });
  };

  const init = (scope = document) => {
    scope.querySelectorAll?.(SELECTOR).forEach((root) => {
      if (root.hasAttribute(READY)) return;
      root.emarvilleDependentVariants = new EmarvilleDependentVariants(root);
    });

    initVisualSelectors(scope);
  };

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches?.(SELECTOR)) init(node.parentElement || document);
        else init(node);
      }
    }
  });

  const start = () => {
    init();
    observer.observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();